import type {
  Address,
  AddressReview,
  AdminAccount,
  Appointment,
  AppUser,
  Article,
  AuthenticatedAdmin,
  City,
  CompanyPerformance,
  Company,
  Country,
  FlaggedReview,
  Formula,
  NotificationLogEntry,
  NotificationTemplate,
  Offer,
  OfferTransaction,
  PerformanceTotals,
  PregnancyWeek,
} from '@domain/entities';
import type { Page } from '@domain/value-objects';
import type {
  AddressDto,
  AddressReviewDto,
  AdminDto,
  AppointmentDto,
  AppUserDto,
  ArticleDto,
  CityDto,
  CompanyDto,
  CountryDto,
  FormulaDto,
  NotificationLogDto,
  NotificationTemplateDto,
  OfferDto,
  PageDto,
  PerformanceRowDto,
  PerformanceTotalsDto,
  PregnancyWeekDto,
  ReviewDto,
  SessionDto,
  TransactionDto,
} from '../dto';

export function toPage<TDto, TEntity>(
  dto: PageDto<TDto>,
  map: (item: TDto) => TEntity,
): Page<TEntity> {
  return {
    items: dto.data.map(map),
    total: dto.meta.total,
    page: dto.meta.page,
    pageSize: dto.meta.pageSize,
    pageCount: Math.max(1, dto.meta.pageCount),
  };
}

function initialsOf(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

export const toAppointment = (dto: AppointmentDto): Appointment => ({ ...dto });

export const toCompany = (dto: CompanyDto): Company => ({ ...dto });

export const toOffer = (dto: OfferDto): Offer => ({
  id: dto.id,
  title: dto.title,
  company: dto.companyName,
  ownerType: dto.ownerType,
  category: dto.category,
  pricing: dto.pricing,
  priceAmount: dto.priceAmount,
  description: dto.description,
  clicks: dto.clicks,
  viewsCount: dto.viewsCount,
  callClicksCount: dto.callClicksCount,
  whatsappClicksCount: dto.whatsappClicksCount,
  emailClicksCount: dto.emailClicksCount,
  status: dto.status,
  publishedAt: dto.publishedAt,
  expiresAt: dto.expiresAt,
  createdAt: dto.createdAt,
  photoUrls: dto.photoUrls ?? [],
  photoKeys: dto.photoKeys ?? [],
  isPaid: dto.isPaid ?? false,
});

export const toArticle = (dto: ArticleDto): Article => ({
  id: dto.id,
  title: dto.title,
  excerpt: dto.excerpt,
  body: dto.body,
  categoryId: dto.category?.id ?? null,
  categoryLabel: dto.category?.name ?? null,
  coverUrl: dto.coverUrl ?? null,
  publishedAt: dto.publishedAt,
  isFeatured: dto.isFeatured,
  imageUrls: dto.imageUrls ?? [],
  imageKeys: dto.imageKeys ?? [],
});

export const toAddress = (dto: AddressDto): Address => ({
  id: dto.id,
  name: dto.name,
  category: dto.category,
  city: dto.city,
  address: dto.address,
  phone: dto.phone,
  whatsapp: dto.whatsapp,
  rating: dto.rating,
  reviewsCount: dto.reviewsCount,
  publishedAt: dto.publishedAt,
});

export const toAddressReview = (dto: AddressReviewDto): AddressReview => ({ ...dto });

export const toReview = (dto: ReviewDto): FlaggedReview => ({
  id: dto.id,
  author: dto.author,
  address: dto.addressName,
  reason: dto.reason,
  comment: dto.comment,
});

export const toAppUser = (dto: AppUserDto): AppUser => ({
  id: dto.id,
  name: dto.name,
  initials: initialsOf(dto.name),
  avatarUrl: dto.avatarUrl,
  city: dto.city,
  countryCode: dto.countryCode,
  email: dto.email,
  phone: dto.phone,
  role: dto.role,
  dueDate: dto.dueDate,
  babySex: dto.babySex,
  isFirstChild: dto.isFirstChild,
  age: dto.age,
  wantsWeeklyTips: dto.wantsWeeklyTips,
  wantsLocalNews: dto.wantsLocalNews,
  locale: dto.locale,
  pregnancy: dto.pregnancy,
  status: dto.status,
  registeredAt: dto.registeredAt,
});

export const toAdmin = (dto: AdminDto): AdminAccount => ({ ...dto });

export const toAuthenticatedAdmin = (dto: SessionDto): AuthenticatedAdmin => ({
  id: dto.admin.id,
  name: dto.admin.name,
  email: dto.admin.email,
  role: dto.admin.role,
  initials: initialsOf(dto.admin.name),
  rights: dto.admin.rights,
});

export const toFormula = (dto: FormulaDto): Formula => ({
  id: dto.id,
  slug: dto.slug,
  label: dto.label,
  description: dto.description,
  amountXof: dto.amountXof,
  durationDays: dto.durationDays,
  sortOrder: dto.sortOrder,
  isActive: dto.isActive,
});

export const toCity = (dto: CityDto): City => ({
  id: dto.id,
  name: dto.name,
  countryCode: dto.countryCode,
  isActive: dto.isActive,
});

export const toCountry = (dto: CountryDto): Country => ({
  id: dto.id,
  name: dto.name,
  code: dto.code,
  digitLen: dto.digitLen,
  example: dto.example,
  providerFeePercent: dto.providerFeePercent ?? 0,
  sortOrder: dto.sortOrder,
  isActive: dto.isActive,
});

export const toPregnancyWeek = (dto: PregnancyWeekDto): PregnancyWeek => ({
  week: dto.week,
  title: dto.title ?? '',
  body: dto.body ?? '',
  fruit: dto.fruit ?? '',
  tailleCm: dto.tailleCm ?? null,
  poidsG: dto.poidsG ?? null,
  moisIndex: dto.moisIndex ?? null,
  anecdote: dto.anecdote ?? null,
  tipsToDo: dto.tipsToDo ?? [],
  tipsToAvoid: dto.tipsToAvoid ?? [],
  imageUrl: dto.imageUrl ?? null,
  imageKey: dto.imageKey ?? null,
  illustrationSvg: dto.illustrationSvg ?? null,
  illustrationScalePct: dto.illustrationScalePct ?? null,
  validated: dto.validated,
});

export const toNotificationTemplate = (dto: NotificationTemplateDto): NotificationTemplate => ({
  ...dto,
});

export const toNotificationLog = (dto: NotificationLogDto): NotificationLogEntry => ({
  id: dto.id,
  type: dto.type,
  company: dto.companyName,
  subject: dto.subject,
  channel: dto.channel,
  sentAt: dto.sentAt,
});

export const toPerformanceRow = (dto: PerformanceRowDto): CompanyPerformance => ({
  id: dto.companyId,
  name: dto.name,
  type: dto.type,
  city: dto.city,
  formulaLabel: dto.formulaLabel,
  offersCount: dto.offersCount,
  revenue: dto.revenue,
  revenueSubscriptions: dto.revenueSubscriptions ?? 0,
  revenueOffers: dto.revenueOffers ?? 0,
});

export const toPerformanceTotals = (dto: PerformanceTotalsDto): PerformanceTotals => ({ ...dto });

export const toTransaction = (dto: TransactionDto): OfferTransaction => ({
  id: dto.id,
  userId: dto.userId,
  userName: dto.userName,
  companyName: dto.companyName,
  offerTitle: dto.offerTitle,
  usedAt: dto.usedAt,
  clicks: dto.clicks,
});
